import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { api, isAuthenticated, getCurrentUser } from '../utils/api';
import { selectClientForUser } from '../utils/clientSelection';

interface ClientLocation {
  label: string;
  location: string;
}

interface Client {
  clientName: string;
  name: string;
  primaryDomain: string;
  promptCount: number;
  domains: string[];
  competitors: Array<{ name: string; urls: string[] }>;
  businessSummary: string;
  locations?: ClientLocation[];
  /** When set in config and locations are empty, backend merges a single regional location (see `geo_scope` in clients.json). */
  geo_scope?: { label?: string; location_key?: string; url?: string };
  /** Extra spellings or short names used with `name` for brand matching in AI Overview / sentiment (optional). */
  brand_aliases?: string[];
  /** Default geographic scope for AI Overview / SerpAPI when not tied to a multi-location row (from backend config). */
  customerRegion?: { scope: 'united_states' | 'state'; state?: string };
}

export interface RestrictedToLocation {
  clientName: string;
  location: string;
}

interface ClientContextType {
  clients: Client[];
  selectedClient: Client | null;
  setSelectedClient: (client: Client | null) => void;
  refreshClients: (preferredClientName?: string) => Promise<Client[]>;
  selectedLocation: string;
  setSelectedLocation: (location: string) => void;
  loading: boolean;
  restrictedToLocation: RestrictedToLocation | null;
  locationRestricted: boolean;
  quarterlyReportEnabled: boolean;
  setQuarterlyReportEnabled: (enabled: boolean) => void;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider: React.FC<{ children: ReactNode; authUserEmail?: string | null }> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedLocation, setSelectedLocationState] = useState('default');
  const [loading, setLoading] = useState(true);
  const [restrictedToLocation, setRestrictedToLocation] = useState<RestrictedToLocation | null>(null);
  const [quarterlyReportEnabled, setQuarterlyReportEnabledState] = useState(true);
  const selectedClientRef = useRef<Client | null>(null);

  useEffect(() => {
    selectedClientRef.current = selectedClient;
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) {
      setSelectedLocationState('default');
      return;
    }
    if (restrictedToLocation && selectedClient.clientName === restrictedToLocation.clientName) {
      setSelectedLocationState(restrictedToLocation.location);
      return;
    }
    const clientKey = `selectedClientLocation:${selectedClient.clientName}`;
    const stored = typeof window !== 'undefined' ? localStorage.getItem(clientKey) : null;
    const available = selectedClient.locations || [];
    const defaultLocation = available[0]?.location || 'default';

    // No configured locations: always aggregate view and clear stale persisted city keys.
    let resolved: string;
    if (available.length === 0) {
      resolved = 'default';
    } else {
      resolved = stored && available.some((loc) => loc.location === stored) ? stored : defaultLocation;
      if (!available.some((loc) => loc.location === resolved)) {
        resolved = defaultLocation;
      }
    }

    setSelectedLocationState(resolved);
    if (typeof window !== 'undefined') {
      localStorage.setItem(clientKey, resolved);
    }
  }, [selectedClient, restrictedToLocation]);

  const refreshClients = useCallback(async (preferredClientName?: string): Promise<Client[]> => {
    try {
      setLoading(true);
      if (!isAuthenticated()) {
        setClients([]);
        setSelectedClient(null);
        setLoading(false);
        return [];
      }
      const response = await api.get('/api/clients');
      const res = response as { clients?: Client[]; data?: { clients?: Client[] }; restrictedToLocation?: RestrictedToLocation };
      const fromData = res?.data?.clients;
      const clientsData: Client[] = Array.isArray(res?.clients) ? res.clients : Array.isArray(fromData) ? fromData : [];
      const restriction = res?.restrictedToLocation ?? (res as { data?: { restrictedToLocation?: RestrictedToLocation } })?.data?.restrictedToLocation ?? null;
      setRestrictedToLocation(restriction);
      setClients(clientsData);

      if (clientsData.length > 0) {
        const storedClientName = localStorage.getItem('selectedClientName');
        const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
        const user = getCurrentUser();
        const clientToSelect = restriction
          ? clientsData.find((c) => c.clientName === restriction.clientName) || clientsData[0]
          : selectClientForUser(clientsData, {
              userEmail: user?.email ?? null,
              storedClientName,
              hostname,
              preferredClientName: preferredClientName ?? null
            });
        if (clientToSelect) {
          if (!selectedClientRef.current || selectedClientRef.current.clientName !== clientToSelect.clientName) {
            setSelectedClient(clientToSelect);
            localStorage.setItem('selectedClientName', clientToSelect.clientName);
          } else {
            const updatedClient = clientsData.find((c: Client) => c.clientName === selectedClientRef.current?.clientName);
            if (updatedClient) {
              setSelectedClient(updatedClient);
              localStorage.setItem('selectedClientName', updatedClient.clientName);
            }
          }
        }
        if (restriction) {
          setSelectedLocationState(restriction.location);
        }
      } else {
        setSelectedClient(null);
        localStorage.removeItem('selectedClientName');
      }
      setLoading(false);
      return clientsData;
    } catch (error: any) {
      if (error?.message?.includes('Authentication') || error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('Invalid or expired token')) {
        setClients([]);
        setSelectedClient(null);
      } else {
        console.error('Error fetching clients:', error);
        setClients([]);
      }
      setLoading(false);
      return [];
    }
  }, []);

  useEffect(() => {
    refreshClients();
  }, [refreshClients]);

  const handleSetSelectedClient = (client: Client | null) => {
    if (restrictedToLocation && client && client.clientName !== restrictedToLocation.clientName) {
      return;
    }
    if (restrictedToLocation && !client) return;
    setSelectedClient(client);
    if (client) {
      localStorage.setItem('selectedClientName', client.clientName);
    } else {
      localStorage.removeItem('selectedClientName');
    }
  };

  const handleSetSelectedLocation = (location: string) => {
    if (restrictedToLocation) return;
    const nextLocation = location && location.trim() ? location : 'default';
    setSelectedLocationState(nextLocation);
    if (selectedClient) {
      localStorage.setItem(`selectedClientLocation:${selectedClient.clientName}`, nextLocation);
    }
  };

  const setQuarterlyReportEnabled = useCallback((enabled: boolean) => {
    setQuarterlyReportEnabledState(enabled);
  }, []);

  useEffect(() => {
    const clientName = selectedClient?.clientName;
    if (!clientName) {
      setQuarterlyReportEnabledState(true);
      return;
    }
    let cancelled = false;
    api
      .get(`/api/generative-seo/${encodeURIComponent(clientName)}/recommendation-settings`)
      .then((response: { quarterly_report_enabled?: unknown; quarterlyReportEnabled?: unknown }) => {
        if (cancelled) return;
        const raw = response.quarterly_report_enabled ?? response.quarterlyReportEnabled;
        setQuarterlyReportEnabledState(
          raw === false || raw === 0 || raw === 'false' || raw === 'f' ? false : true
        );
      })
      .catch(() => {
        if (!cancelled) setQuarterlyReportEnabledState(true);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClient?.clientName]);

  useEffect(() => {
    const onFeatureSettingsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ quarterlyReportEnabled?: boolean }>).detail;
      if (typeof detail?.quarterlyReportEnabled === 'boolean') {
        setQuarterlyReportEnabledState(detail.quarterlyReportEnabled);
      }
    };
    window.addEventListener('aeo:feature-settings-changed', onFeatureSettingsChanged);
    return () => window.removeEventListener('aeo:feature-settings-changed', onFeatureSettingsChanged);
  }, []);

  return (
    <ClientContext.Provider
      value={{
        clients,
        selectedClient,
        setSelectedClient: handleSetSelectedClient,
        refreshClients,
        selectedLocation,
        setSelectedLocation: handleSetSelectedLocation,
        loading,
        restrictedToLocation,
        locationRestricted: !!restrictedToLocation,
        quarterlyReportEnabled,
        setQuarterlyReportEnabled,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

